import Phaser from 'phaser';
import { getGameContext } from '../utils/context';
import { BGM, SFX } from '../audio/AudioKeys';
import { initSceneContext } from '../utils/initContext';
import type { GameOverData } from '../types/GameOverData';
import { formatMs } from '../utils/time';

export class GameOverScene extends Phaser.Scene {
    private enterKey!: Phaser.Input.Keyboard.Key;
    private spaceKey!: Phaser.Input.Keyboard.Key;
    private survivalMs: number = 0;

    constructor() {
        super({ key: 'GameOverScene' });
    }

    init(data: GameOverData) {
        this.survivalMs = (data && typeof data.survivalMs === 'number') ? data.survivalMs : 0;
    }

    create() {
        // Ensure GameContext and SoundManager are initialized for this scene
        initSceneContext(this);

        const { width, height } = this.cameras.main;

        // Title
        this.add
            .text(width / 2, height / 2 - 100, 'Game Over', { fontSize: '64px', color: '#ffffff' })
            .setOrigin(0.5);

        // Final survival time
        const msg = `Survived: ${formatMs(this.survivalMs)}`;
        const timeStyle: Phaser.Types.GameObjects.Text.TextStyle = {
            fontFamily: 'monospace',
            fontSize: '36px',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 4,
            align: 'center'
        };
        this.add.text(width / 2, height / 2 - 30, msg, timeStyle).setOrigin(0.5, 0.5);

        // Instructions
        this.add
            .text(width / 2, height / 2 + 40, 'Press ENTER for Main Menu\nPress SPACE to Restart', {
                fontSize: '20px',
                color: '#ffffff',
                align: 'center'
            })
            .setOrigin(0.5);

        // Optional sting on entering scene
        try {
            const ctx = getGameContext(this);
            ctx.sound?.playSfx(SFX.GameOver, { volume: 0.7 });
        } catch {}

        // Clickable buttons (optional but user-friendly)
        const mainMenuBtn = this.add
            .text(width / 2, height / 2 + 80, 'Main Menu', { fontSize: '28px', color: '#ffffff' })
            .setOrigin(0.5)
            .setInteractive({ useHandCursor: true });

        const restartBtn = this.add
            .text(width / 2, height / 2 + 120, 'Restart', { fontSize: '28px', color: '#ffffff' })
            .setOrigin(0.5)
            .setInteractive({ useHandCursor: true });

        // Hover effects
        const onOver = (txt: Phaser.GameObjects.Text) => txt.setColor('#ffff00');
        const onOut = (txt: Phaser.GameObjects.Text) => txt.setColor('#ffffff');
        mainMenuBtn.on('pointerover', () => onOver(mainMenuBtn));
        mainMenuBtn.on('pointerout', () => onOut(mainMenuBtn));
        restartBtn.on('pointerover', () => onOver(restartBtn));
        restartBtn.on('pointerout', () => onOut(restartBtn));

        // Click handlers
        mainMenuBtn.on('pointerdown', () => {
            try {
                const ctx = getGameContext(this);
                ctx.sound?.playSfx(SFX.UiClick);
                ctx.sound?.playMusic(BGM.Menu, { loop: true, fadeInMs: 400, volume: 0.5 });
            } catch {}
            this.goToMainMenu();
        });
        restartBtn.on('pointerdown', () => {
            try {
                const ctx = getGameContext(this);
                ctx.sound?.playSfx(SFX.UiClick);
            } catch {}
            this.restartGame();
        });

        // Keyboard handlers (guard for possibly-null keyboard)
        const kb = this.input.keyboard;
        if (kb) {
            this.enterKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);
            this.spaceKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

            this.enterKey.once('down', () => this.goToMainMenu());
            this.spaceKey.once('down', () => this.restartGame());
        }
    }

    private goToMainMenu() {
        // Transition back to Main Menu; starting the scene resets its state naturally
        this.scene.start('MainMenuScene');
    }

    private restartGame() {
        // Start a new run by starting GameScene directly
        this.scene.start('GameScene');
    }
}
